/**
 * fetch wrapper for the Farmgate API.
 *
 * - Reads the JWT session from localStorage (`farmgate-auth`).
 * - Attaches the bearer access token.
 * - On a 401 it transparently refreshes the token once (deduped across
 *   concurrent requests) and retries.
 */
import { API_BASE, AUTH_STORAGE_KEY } from './config';

export function readAuth() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY)) || null;
  } catch {
    return null;
  }
}

export function writeAuth(session) {
  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* ignore quota/availability errors */
  }
}

export function clearAuth() {
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

let refreshPromise = null;

async function refreshSession() {
  const auth = readAuth();
  if (!auth?.refreshToken) return null;
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: auth.refreshToken }),
  });
  if (!res.ok) {
    clearAuth();
    return null;
  }
  const data = await res.json();
  const next = {
    user: data.user,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  };
  writeAuth(next);
  return next;
}

/**
 * Perform an API request. Pass `auth: false` for unauthenticated endpoints
 * (login/refresh/public). Returns parsed JSON, or null for 204.
 */
export async function apiFetch(path, { method = 'GET', body, auth = true, headers = {} } = {}) {
  const send = async (token) => {
    const h = { 'Content-Type': 'application/json', ...headers };
    if (auth && token) h.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, {
      method,
      headers: h,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  let res = await send(readAuth()?.accessToken);

  if (res.status === 401 && auth && readAuth()?.refreshToken) {
    if (!refreshPromise) {
      refreshPromise = refreshSession().finally(() => {
        refreshPromise = null;
      });
    }
    const refreshed = await refreshPromise;
    if (refreshed) res = await send(refreshed.accessToken);
  }

  if (!res.ok) {
    let detail;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  return res.status === 204 ? null : res.json();
}
